import type React from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { Button, type ButtonProps, IconButton } from "./button";
import type { IconSymbol } from "./icon";

export interface ValidationError {
  type: "file-type" | "file-size";
  title: string;
  description?: string;
}

interface UploadFileProps extends ButtonProps {
  fileTypes: string[];
  onFileSelect?: (file: File) => void;
  onValidationError?: (error: ValidationError) => void;
  maxFileSize?: number;
  children?: React.ReactNode;
  icon?: IconSymbol;
  size?: "xs" | "sm" | "default";
  fileSizeErrorText?: string;
  fileTypeErrorText?: string;
}

const UploadFileButton: React.FC<UploadFileProps> = ({
  className,
  loading = false,
  disabled,
  children,
  fileTypes,
  onFileSelect,
  onValidationError,
  maxFileSize,
  icon = "attachment" as IconSymbol,
  size = "default",
  fileSizeErrorText,
  fileTypeErrorText,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isValidFileType = (file: File): boolean => {
    return fileTypes.some((type) => {
      // Handle wildcard patterns (e.g., "image/*", "video/*")
      if (type.endsWith("/*")) {
        const baseType = type.split("/")[0];
        return file.type.startsWith(`${baseType}/`);
      }

      // Handle specific extensions (e.g., ".pdf", ".doc")
      if (type.startsWith(".")) {
        const fileName = file.name.toLowerCase();
        return fileName.endsWith(type.toLowerCase());
      }

      // Handle specific MIME types (e.g., "application/pdf")
      return file.type === type;
    });
  };

  const getAcceptableFileTypesMessage = (): string => {
    const formattedTypes = fileTypes.map((type) => {
      if (type.startsWith(".")) {
        return type.toUpperCase(); // For extensions like .PDF
      }
      if (type.endsWith("/*")) {
        return t(`file_type_${type.split("/")[0]}`); // Translation for "Images", "Videos", etc.
      }
      return type; // For specific MIME types
    });

    return formattedTypes.join(", ");
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const file = event.target.files?.[0];

    if (!file) return;

    // Validate file type
    if (!isValidFileType(file)) {
      onValidationError?.({
        type: "file-type",
        title: fileTypeErrorText ?? t("invalid_file_type"),
        description: !fileTypeErrorText
          ? t("please_select_valid_file", {
              types: getAcceptableFileTypesMessage(),
            })
          : undefined,
      });

      if (event.target) {
        event.target.value = "";
      }
      return;
    }

    // Validate file size
    if (maxFileSize && file.size > maxFileSize) {
      onValidationError?.({
        type: "file-size",
        title: fileSizeErrorText ?? t("file_too_large"),
        description: !fileSizeErrorText
          ? t("max_file_size", {
              size: `${(maxFileSize / (1024 * 1024)).toFixed(2)}MB`,
            })
          : undefined,
      });

      if (event.target) {
        event.target.value = "";
      }
      return;
    }

    onFileSelect?.(file);

    if (event.target) {
      event.target.value = "";
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Prevent event from bubbling up to the form
    e.preventDefault();
    e.stopPropagation();

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const fileInput = (
    <input
      type="file"
      ref={fileInputRef}
      className="hidden"
      onChange={handleFileChange}
      accept={fileTypes?.join(", ")}
    />
  );

  return (
    <div className={cn("flex items-center", className)}>
      {fileInput}
      {children ? (
        <Button
          type="button"
          variant="secondary"
          onClick={handleClick}
          loading={loading}
          disabled={loading || disabled}
          leftIcon={icon}
          className="w-full"
          size={size}
        >
          {children}
        </Button>
      ) : (
        <IconButton
          type="button"
          variant="secondary"
          onClick={handleClick}
          loading={loading}
          disabled={loading || disabled}
          icon={icon}
          size={size}
        />
      )}
    </div>
  );
};

export { UploadFileButton };
